import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationContext";
import { useBulletinUnread } from "@/context/BulletinContext";
import api from "@/lib/api";
import { hasLogisticsAccess, isSupervisorOrAbove, isAdminOrAbove, isCommandingOfficer, hasRole, isTopTier } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  House, CalendarBlank, ClipboardText, Package, FileText, ShieldCheck,
  ChartBar, GearSix, SignOut, ListChecks, ClockCounterClockwise, Stack, Bell, ShieldStar, Key, ArrowsLeftRight, Users, IdentificationCard, Megaphone, ChalkboardTeacher, EnvelopeSimple, User as UserIcon
} from "@phosphor-icons/react";

const LOGO = "https://customer-assets.emergentagent.com/job_bedstuy-ems-hub/artifacts/rcm1qmbk_BSVAC%20Patch.png";

const ROLE_COLORS = {
  super_admin: "bg-indigo-900 text-white",
  admin: "bg-slate-900 text-white",
  commanding_officer: "bg-rose-700 text-white",
  staff_chief: "bg-fuchsia-700 text-white",
  supervisor: "bg-blue-600 text-white",
  logistics: "bg-amber-600 text-white",
  volunteer: "bg-emerald-700 text-white",
};

const ROLE_LABEL = {
  commanding_officer: "COMMANDING OFFICER",
  staff_chief: "STAFF CHIEF",
  super_admin: "SUPER ADMIN",
};

function NavItem({ to, icon: Icon, label, testid, end, badge, onNavigate, blink }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testid}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 text-sm border-l-2 transition-colors ${
          isActive
            ? "bg-slate-100 border-slate-900 text-slate-900 font-semibold"
            : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
        } ${blink ? "nav-blink-red" : ""}`
      }
    >
      <Icon size={18} weight="bold" />
      <span className="flex-1">{label}</span>
      {badge != null && badge > 0 && (
        <span className={`text-white text-[10px] font-bold rounded-sm px-1.5 py-0.5 mono ${blink ? "bg-red-600 nav-badge-pulse" : "bg-emerald-600"}`} data-testid={`${testid}-badge`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen = false, onMobileClose = () => {} }) {
  const { user, logout } = useAuth();
  const { unread } = useNotifications();
  const { unread: bulletinUnread } = useBulletinUnread();
  const nav = useNavigate();
  const role = user?.role || "volunteer";
  // Auto-close the drawer on a mobile navigation tap so operators land
  // on the destination page with content visible instead of a still-open
  // sheet covering it.
  const closeOnNav = () => onMobileClose();

  // Applications badge — only admin-tier users see the entry, so we
  // scope the polling to those roles too. Polls every 45s and refetches
  // when the URL changes so opening the page immediately drops the
  // counter.
  const canSeeApps = !!user && (isAdminOrAbove(user) || hasRole(user, "admin"));
  const [appsUnread, setAppsUnread] = useState(0);
  useEffect(() => {
    if (!canSeeApps) { setAppsUnread(0); return; }
    let alive = true;
    const load = () => api.get("/applications/unread-count")
      .then((r) => { if (alive) setAppsUnread(r.data?.count || 0); })
      .catch(() => {});
    load();
    const t = setInterval(load, 45000);
    // React-router history listener — refetch on any path change so
    // opening an application immediately updates the sidebar.
    const onHash = () => load();
    window.addEventListener("popstate", onHash);
    window.addEventListener("applications-updated", onHash);
    return () => { alive = false; clearInterval(t); window.removeEventListener("popstate", onHash); window.removeEventListener("applications-updated", onHash); };
  }, [canSeeApps, user?.id]);

  return (
    <>
      {/* Backdrop shown only when the drawer is open on mobile — tap-out
          closes the sidebar. Hidden on md+ where the sidebar is a
          permanent left rail. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/50 md:hidden"
          onClick={onMobileClose}
          data-testid="sidebar-backdrop"
          aria-hidden
        />
      )}
      <aside
        className={`
          fixed z-40 inset-y-0 left-0 w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          md:static md:translate-x-0
        `}
        data-testid="sidebar"
      >
      <div className="px-4 py-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <img src={LOGO} alt="BSVAC / CBVAC" className="h-12 w-auto" data-testid="sidebar-logo" />
          <div className="leading-tight">
            <div className="font-display text-sm font-black tracking-tight text-slate-900">BSVAC / CBVAC</div>
            <div className="tab-tag text-slate-500 mt-1">Operations Console</div>
          </div>
        </div>
      </div>

      <nav className="py-2 flex-1 overflow-y-auto">
        {/* A "pure" Admin (admin role, without supervisor / top-tier stacking)
            gets a stripped-down sidebar: only Documents & Certifications and
            the Admin section. They are an office role — no schedule, no
            supervisor tools, no operational tabs. */}
        {(() => {
          const pureAdmin = hasRole(user, "admin") && !isTopTier(user) && !hasRole(user, "supervisor");
          if (pureAdmin) {
            return (
              <>
                <div className="px-3 py-1 py-1 tab-tag text-slate-500">Documents and Certifications</div>
                <NavItem to="/documents" icon={FileText} label="Documents" testid="nav-documents" onNavigate={closeOnNav} />
                <NavItem to="/compliance" icon={ShieldStar} label="Paperwork & Compliance" testid="nav-compliance" onNavigate={closeOnNav} />
                {user?.is_original_admin && (
                  <NavItem to="/validation" icon={Key} label="Validation Tool" testid="nav-validation" onNavigate={closeOnNav} />
                )}
                {hasLogisticsAccess(user) && (
                  <>
                    <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Logistics</div>
                    <NavItem to="/inventory" icon={Package} label="Inventory" testid="nav-inventory" onNavigate={closeOnNav} />
                    <NavItem to="/supply-requests" icon={ClipboardText} label="Supply Requests" testid="nav-supply" onNavigate={closeOnNav} />
                  </>
                )}
                <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Admin</div>
                <NavItem to="/admin" icon={GearSix} label="Admin & Settings" testid="nav-admin" onNavigate={closeOnNav} />
                <NavItem to="/admin/applications" icon={EnvelopeSimple} label="Applications" testid="nav-applications" badge={appsUnread} blink={appsUnread > 0} onNavigate={closeOnNav} />
                <NavItem to="/admin/reports" icon={ChartBar} label="Admin Reports" testid="nav-admin-reports" onNavigate={closeOnNav} />
                <NavItem to="/training" icon={ChalkboardTeacher} label="Training" testid="nav-training" onNavigate={closeOnNav} />
                <NavItem to="/settings" icon={UserIcon} label="My Profile" testid="nav-settings" onNavigate={closeOnNav} />
              </>
            );
          }
          return (
            <>
              <div className="px-3 py-1 tab-tag text-slate-500">Main</div>
              <NavItem end to="/" icon={House} label="Dashboard" testid="nav-dashboard" onNavigate={closeOnNav} />
              <NavItem to="/inbox" icon={Bell} label="Inbox" testid="nav-inbox" badge={unread} onNavigate={closeOnNav} />
              <NavItem to="/bulletins" icon={Megaphone} label="Bulletin Board" testid="nav-bulletins" badge={bulletinUnread} blink={bulletinUnread > 0} onNavigate={closeOnNav} />
              <NavItem to="/schedule" icon={CalendarBlank} label="Schedule" testid="nav-schedule" onNavigate={closeOnNav} />
              {isSupervisorOrAbove(user) && (
                <NavItem to="/schedule/templates" icon={Stack} label="Schedule Templates" testid="nav-templates" onNavigate={closeOnNav} />
              )}
              <NavItem to="/my-tours" icon={ListChecks} label="My Schedule" testid="nav-my-tours" onNavigate={closeOnNav} />
              <NavItem to="/on-now" icon={Users} label="On Now" testid="nav-on-now" onNavigate={closeOnNav} />
              <NavItem to="/roster" icon={IdentificationCard} label="Roster" testid="nav-roster" onNavigate={closeOnNav} />
              <NavItem to="/trades" icon={ArrowsLeftRight} label={isSupervisorOrAbove(user) ? "Trade & Drop Board" : "Trade Board"} testid="nav-trades" onNavigate={closeOnNav} />
              <NavItem to="/settings" icon={UserIcon} label="My Profile" testid="nav-settings" onNavigate={closeOnNav} />

              {hasLogisticsAccess(user) && (
                <>
                  <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Logistics</div>
                  <NavItem to="/inventory" icon={Package} label="Inventory" testid="nav-inventory" onNavigate={closeOnNav} />
                  <NavItem to="/supply-requests" icon={ClipboardText} label="Supply Requests" testid="nav-supply" onNavigate={closeOnNav} />
                </>
              )}

              <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Documents and Certifications</div>
              <NavItem to="/documents" icon={FileText} label="Documents" testid="nav-documents" onNavigate={closeOnNav} />
              <NavItem to="/compliance" icon={ShieldStar} label="Paperwork & Compliance" testid="nav-compliance" onNavigate={closeOnNav} />
              {(isCommandingOfficer(user) || user?.is_original_admin) && (
                <NavItem to="/validation" icon={Key} label="Validation Tool" testid="nav-validation" onNavigate={closeOnNav} />
              )}

              {isSupervisorOrAbove(user) && (
                <>
                  <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Supervisor</div>
                  <NavItem to="/supervisor" icon={ShieldCheck} label="Supervisor Hub" testid="nav-supervisor" onNavigate={closeOnNav} />
                  <NavItem to="/training" icon={ChalkboardTeacher} label="Training" testid="nav-training" onNavigate={closeOnNav} />
                  <NavItem to="/audit" icon={ClockCounterClockwise} label="Audit Trail" testid="nav-audit" onNavigate={closeOnNav} />
                </>
              )}
              {/* iter35.1 — Members who are authorized to teach at least
                  one class (non-empty instructor_classes) get the
                  Training tab in a dedicated section even if they're
                  not supervisors. Supervisors+ already see Training
                  under the Supervisor section above, so we don't
                  double-render for them. */}
              {!isSupervisorOrAbove(user) && (user?.instructor_classes?.length > 0) && (
                <>
                  <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Instructor</div>
                  <NavItem to="/training" icon={ChalkboardTeacher} label="Training" testid="nav-training" onNavigate={closeOnNav} />
                </>
              )}

              {isAdminOrAbove(user) && (
                <>
                  <div className="px-3 py-1 mt-3 tab-tag text-slate-500">Admin</div>
                  <NavItem to="/admin" icon={GearSix} label="Admin & Settings" testid="nav-admin" onNavigate={closeOnNav} />
                  <NavItem to="/admin/applications" icon={EnvelopeSimple} label="Applications" testid="nav-applications" badge={appsUnread} blink={appsUnread > 0} onNavigate={closeOnNav} />
                  <NavItem to="/admin/reports" icon={ChartBar} label="Admin Reports" testid="nav-admin-reports" onNavigate={closeOnNav} />
                </>
              )}
            </>
          );
        })()}
      </nav>

      <div className="border-t border-slate-200 p-3 flex items-center gap-2">
        <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center font-display font-bold text-sm" aria-hidden>
          {user?.name?.[0] || "U"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate" data-testid="user-name">{user?.name}</div>
          <Badge className={`mt-0.5 rounded-sm ${ROLE_COLORS[role] || "bg-slate-700 text-white"} text-[10px] tracking-widest`} data-testid="user-role">
            {ROLE_LABEL[role] || role.toUpperCase()}
          </Badge>
        </div>
        <Button
          data-testid="logout-btn"
          size="icon"
          variant="ghost"
          onClick={async () => { await logout(); nav("/login"); }}
          title="Logout"
        >
          <SignOut size={18} weight="bold" />
        </Button>
      </div>
    </aside>
    </>
  );
}
